import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { loadCarousel, selectCarouselItems, selectCarouselLoading } from '../store/slices/carouselSlice';
import './Hero.css';

const Hero = () => {
  const dispatch = useDispatch();
  const carouselItems = useSelector(selectCarouselItems);
  const loading = useSelector(selectCarouselLoading);
  const [currentIndex, setCurrentIndex] = useState(0);

  // Load carousel items on component mount - always fetch fresh data
  useEffect(() => {
    dispatch(loadCarousel());
  }, [dispatch]);

  // Auto-play carousel
  useEffect(() => {
    if (carouselItems.length === 0) return;
    
    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => 
        prevIndex === carouselItems.length - 1 ? 0 : prevIndex + 1
      );
    }, 5000); // Change slide every 5 seconds

    return () => clearInterval(interval);
  }, [carouselItems.length]);

  const goToSlide = (index) => {
    setCurrentIndex(index);
  };

  const goToPrevious = () => {
    setCurrentIndex((prevIndex) => 
      prevIndex === 0 ? carouselItems.length - 1 : prevIndex - 1
    );
  };

  const goToNext = () => {
    setCurrentIndex((prevIndex) => 
      prevIndex === carouselItems.length - 1 ? 0 : prevIndex + 1
    );
  };

  // Show loading state or fallback if no items
  if (loading && carouselItems.length === 0) {
    return (
      <section className="hero-section">
        <div className="carousel-container">
          <div className="carousel-slide active" style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1593113598332-cd288d649433?w=1920&q=80)' }}>
            <div className="hero-overlay">
              <div className="hero-content">
                <h1 className="hero-title">Loading...</h1>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  // If no carousel items, show a default slide
  if (carouselItems.length === 0) {
    return (
      <section className="hero-section">
        <div className="carousel-container">
          <div className="carousel-slide active" style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1593113598332-cd288d649433?w=1920&q=80)' }}>
            <div className="hero-overlay">
              <div className="hero-content">
                <h1 className="hero-title">Your Partner in Modern Farming Solutions.</h1>
                <p className="hero-subtitle">Quality Greenhouse Products & Smart Agriculture Services</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="hero-section">
      <div className="carousel-container">
        {carouselItems.map((item, index) => (
          <div
            key={item.id}
            className={`carousel-slide ${index === currentIndex ? 'active' : ''}`}
            style={{ backgroundImage: `url(${item.image})` }}
          >
            <div className="hero-overlay">
              <div className="hero-content">
                {item.title && <h1 className="hero-title">{item.title}</h1>}
                {item.subtitle && <p className="hero-subtitle">{item.subtitle}</p>}
              </div>
            </div>
          </div>
        ))}
        
        {/* Navigation arrows */}
        {carouselItems.length > 1 && (
          <>
            <button className="carousel-arrow carousel-arrow-left" onClick={goToPrevious}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M15 18l-6-6 6-6"/>
              </svg>
            </button>
            <button className="carousel-arrow carousel-arrow-right" onClick={goToNext}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 18l6-6-6-6"/>
              </svg>
            </button>
          </>
        )}

        {/* Dots indicator */}
        {carouselItems.length > 1 && (
          <div className="carousel-dots">
            {carouselItems.map((_, index) => (
              <button
                key={index}
                className={`carousel-dot ${index === currentIndex ? 'active' : ''}`}
                onClick={() => goToSlide(index)}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default Hero;
